import { useState } from 'react';
import 'react-day-picker/dist/style.css';
import { IFiltersExecutive } from '../../../../interfaces/excecutive/IFiltersExecutive';
import ClairePromptTracking from '../components/excecutive/ClairePromptTracking';
import MonthToDateUsage from '../components/excecutive/MonthToDateUsage';
import SpaAnalyticsTest from '../components/excecutive/SpaAnalyticsTest';
import ClaireMostCallsGraph from '../components/excecutive/ClaireMostCallsGraph';
import {
    useGetClaireKpisQuery,
} from '../../../../store/excecutive/executiveApi';
import { ExecViewChatEntry } from '../exec-view/ExecViewChatEntry';

type CallsFilter = 'all' | 'transferred' | 'successful' | 'failed' | 'abandoned';

const CLAIRE_DASHBOARD_POLL_MS = 7000;

const Claire2Tab = () => {
    const [filters] = useState<IFiltersExecutive>({
        platform: 'all',
        competitor: '',
        date: [],
    });
    const [kpiView, setKpiView] = useState<'today' | '7day' | '30day'>('today');
    const [dashboardView, setDashboardView] = useState<'executive' | 'detailed'>(
        'executive',
    );
    const [callsRecordFilter, setCallsRecordFilter] = useState<CallsFilter>('all');
    /** Bumps when opening Calls Record so the table remounts and refetches. */
    const [callsRecordSessionKey, setCallsRecordSessionKey] = useState(0);

    const openCallsRecord = () => {
        setDashboardView('detailed');
        setCallsRecordSessionKey((k) => k + 1);
    };

    const { data: claireKpisData, isFetching: isFetchingClaireKpis } =
        useGetClaireKpisQuery(
            {
                view: kpiView,
            },
            {
                refetchOnMountOrArgChange: true,
                pollingInterval: CLAIRE_DASHBOARD_POLL_MS,
            },
        );

    return (
        <>
        <div className='p-2 border-[1px] rounded-[8px] border-solid border-[#F5F5F5] dark:border-transparent'>
        <div  className='rounded-sm border-[#00330F] dark:border-borderColor-darkMode pl-2 flex flex-row items-center'>
            <div className='h-[28px] w-[5px] rounded-full bg-[#00330F] dark:bg-[#00691F] mr-1' />
            <div className='flex flex-row items-center bg-[#F7F9FB] dark:bg-black px-2 rounded-[8px] h-[28px] justify-center'>
                <p className='dark:text-primary-darkMode mr-2 font-inter text-[14px] font-medium'>Claire</p>
                <p className=' text-sm font-inter text-[14px] font-normal text-[#A4A7AE]'>AI Reservation Manager</p>
            </div>
        </div>
    </div>

        {dashboardView === 'executive' && (
            <div className="mt-4 mb-2 w-full min-w-0 max-w-full self-stretch">
                <ExecViewChatEntry />
            </div>
        )}

        <div className={`py-5 my-3`}>
            <div className="mb-4">
                <div className="relative rounded-full w-full md:w-fit h-[43px] bg-[#00330F] dark:bg-black p-1 grid grid-cols-2 overflow-hidden">
                    <span
                        className={`absolute top-1 bottom-1 left-1 w-[calc(50%-4px)] rounded-full bg-white dark:bg-bgColor-darkMode transition-transform duration-300 ease-out ${
                            dashboardView === 'detailed' ? 'translate-x-full' : 'translate-x-0'
                        }`}
                    />
                    {[
                        { label: 'Executive Dashboard', value: 'executive' as const },
                        { label: 'Calls Record', value: 'detailed' as const },
                    ].map((item) => (
                        <button
                            key={item.value}
                            type="button"
                            onClick={() =>
                                item.value === 'detailed'
                                    ? openCallsRecord()
                                    : setDashboardView('executive')
                            }
                            className={`relative z-10 h-full px-5 text-sm md:text-base font-medium rounded-full transition-colors ${
                                dashboardView === item.value
                                    ? 'text-[#00330F] dark:text-white'
                                    : 'text-white dark:text-gray-300'
                            }`}>
                            {item.label}
                        </button>
                    ))}
                </div>
            </div>

            {dashboardView === 'executive' ? (
                <>
                    <MonthToDateUsage filters={filters} />
                    <ClairePromptTracking
                        kpis={claireKpisData}
                        kpiView={kpiView}
                        onViewTransferDetails={() => {
                            setCallsRecordFilter('transferred');
                            openCallsRecord();
                        }}
                        onViewBookingFailureDetails={() => {
                            setCallsRecordFilter('failed');
                            openCallsRecord();
                        }}
                        rightNode={
                            <div className="relative w-full max-w-[300px] md:w-[300px] h-[40px] rounded-full bg-[#00330F] p-[3px] grid grid-cols-3 overflow-hidden">
                                <span
                                    className="absolute top-[3px] bottom-[3px] left-[3px] rounded-full bg-white transition-transform duration-300 ease-out"
                                    style={{
                                        width: 'calc((100% - 6px) / 3)',
                                        transform: `translateX(${
                                            kpiView === 'today'
                                                ? '0%'
                                                : kpiView === '7day'
                                                ? '100%'
                                                : '200%'
                                        })`,
                                    }}
                                />
                                {[
                                    { label: 'Today', value: 'today' as const },
                                    { label: '7 Day', value: '7day' as const },
                                    { label: '30 Day', value: '30day' as const },
                                ].map((item) => (
                                    <button
                                        key={item.value}
                                        type="button"
                                        onClick={() => setKpiView(item.value)}
                                        disabled={isFetchingClaireKpis}
                                        className={`relative z-10 h-full px-1 text-center text-[14px] leading-[20px] font-medium transition-colors ${
                                            kpiView === item.value
                                                ? 'text-[#0A0A0A]'
                                                : 'text-white'
                                        }`}
                                        style={{ fontFamily: 'Inter, Arial, sans-serif' }}>
                                        {item.label}
                                    </button>
                                ))}
                            </div>
                        }
                    />
                    <ClaireMostCallsGraph />
                </>
            ) : (
                <SpaAnalyticsTest
                    key={callsRecordSessionKey}
                    tableOnly
                    initialFilter={callsRecordFilter}
                />
            )}
        </div>
        </>
    );
};

export default Claire2Tab;
